import React from 'react';

import autoBind from 'react-autobind';

// import other components within the same project here

import Localization from "../Localization.jsx"
import Config from "../Config.jsx";
import AuthService from "../tools/AuthService.js";
import { renderGoogleButton } from "../tools/googleIdentity.js";
import { signInWithApple, isUserCancelled } from "../tools/appleIdentity.js";
import AppleSignInButton from "./AppleSignInButton.jsx";

import {
  Modal,
  Paper,
  Box,
  TextInput,
  PasswordInput,
  Button,
  Switch,
  Text,
  Group,
  Divider,
  Anchor,
  Alert,
} from '@mantine/core';
import { IconUserCircle, IconLock, IconAlertCircle } from '@tabler/icons-react';

import cuhk_clst from "../images/logos/cuhk_clst.png";
// Bundled rather than fetched. These used to come from `edconnect_button.php`
// on the PHP host, which also acted as a file server; the FastAPI cutover moved
// the API to api.ev-cuhk.net, where that script does not exist — so the button
// rendered as a broken image with no visible label. Importing them makes the
// button independent of which host is answering.
import edconnectButtonEn from "../images/logos/EdConnect_Login_Button_Eng.png";
import edconnectButtonTc from "../images/logos/EdConnect_Login_Button_Chi.png";

import { BRAND_PRIMARY, HALF_TONE, MUTED, DIVIDER } from "../theme/brandTheme.js";

class LoginDialog extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();
    // Container the Google Identity Services button renders into. GIS draws
    // its own iframe button — we never see the account picker, only the
    // credential callback.
    this.googleButtonContainer = React.createRef();

    // state variables here
    this.state = {
      // aNumber : 1,
      // aString: "abc",
      // anArray: [],
      // anObject: {},
      username: "",
      password: "",
      rememberMe: false,
      // Apple's popup can take a while to resolve; the button locks so a second
      // click cannot open a second sheet with a second nonce.
      appleBusy: false,
      showForgotPasswordDialog: false,
      forgotPasswordEmail: "",
      showConfirmationDialog: false,
      confirmationMessage: ""
    };
    autoBind(this);
  }

  componentDidMount() {
    this.mountGoogleButton();
  }

  // reCAPTCHA is only used by the forgot-password flow, so the script is
  // pulled in when that dialog opens rather than on every mount.
  ensureRecaptchaScript() {
    if (document.getElementById("recaptcha-script")) {
      return;
    }
    const script = document.createElement("script");
    script.id = "recaptcha-script";
    script.src = "https://www.google.com/recaptcha/api.js?render=6Lf2P2IrAAAAAGlJXkCwTqHvXG6Qrc_BJuD5gd0X";
    script.async = true;
    document.body.appendChild(script);
  }

  usernameChanged (e) {
      this.setState({
        username: e.target.value
      });
      this.state.username = e.target.value;
  }
  passwordChanged (e) {
      this.setState({
        password: e.target.value
      });
      this.state.password = e.target.value;
  }

  loginClicked() {
    // console.log(this.state.username);
    // console.log(this.state.password);

    this.props.parent.refs.loginCreateSession.sendLoginRequest(
      this.state.username,
      this.state.password,
      this.state.rememberMe,
      this.props.parent.loginOK,
      this.props.parent.loginFailed
    );
  }

  edConnectLoginClicked() {
    // A top-level navigation to the FastAPI start leg, which 302s to EdCity.
    // NOT a fetch: the browser has to follow that redirect to EdCity's own
    // login page, and a cross-origin fetch would swallow it.
    //
    // The return target is origin+pathname with no hash — EdCity's round trip
    // lands the user back at the composer's entry point, and RootPage reads the
    // outcome off window.location.search there.
    //
    // A second press (after a refused student login) is logout-then-login:
    // otherwise EdCity reuses the student SSO cookie and never shows a form.
    window.location.href = AuthService.edCityLoginUrl();
  }

  async appleLoginClicked() {
    // Apple's popup only opens inside the user-gesture that called it, so this
    // must run straight off the click — no awaits before signInWithApple().
    this.setState({ appleBusy: true });
    try {
      const { identityToken, rawNonce } = await signInWithApple({
        locale: Localization.strings.getLanguage(),
      });
      const res = await AuthService.loginWithApple(identityToken, rawNonce);
      this.props.parent.loginOK(res, this.state.rememberMe);
    } catch (e) {
      if (isUserCancelled(e)) {
        // Closing the sheet is not a failure. Saying "sign-in refused" here
        // would tell a teacher their account is broken when they simply
        // changed their mind.
        return;
      }
      console.warn('LoginDialog: Apple sign-in refused', e && (e.error || e.status));
      this.props.root.addNotification(Localization.strings.APPLE_LOGIN_REFUSED, false);
    } finally {
      this.setState({ appleBusy: false });
    }
  }

  mountGoogleButton() {
    if (!(typeof Config.ENABLE_GOOGLE_LOGIN === 'function' && Config.ENABLE_GOOGLE_LOGIN())) {
      return;
    }
    // Pin the button locale to the composer's own language — GIS otherwise
    // renders in the BROWSER locale, which can be neither of ours.
    renderGoogleButton(this.googleButtonContainer.current, this.handleGoogleCredential, {
      locale: Localization.strings.getLanguage() === 'tc' ? 'zh-TW' : 'en',
    })
      .catch((e) => {
        // GIS unreachable (offline, blocked) — hide the section rather than
        // leaving a dead button.
        console.warn('LoginDialog: Google Identity Services failed to load', e);
        this.setState({ googleUnavailable: true });
      });
  }

  handleGoogleCredential(idToken) {
    // The composer sends the id_token straight to the backend. Admission is
    // decided THERE by the role of the account the identity resolves to
    // (link-only, staff-only) — a student or unlinked Google account gets the
    // same generic 401 whatever it is, and the message below tells a teacher
    // what to do about it.
    AuthService.loginWithGoogle(idToken)
      .then((res) => {
        this.props.parent.loginOK(res, this.state.rememberMe);
      })
      .catch((e) => {
        console.warn('LoginDialog: Google sign-in refused', e && e.status);
        this.props.root.addNotification(Localization.strings.GOOGLE_LOGIN_REFUSED, false);
      });
  }

  /**
   * The explanation for a refused third-party sign-in.
   *
   * `not_linked` is the composer's one refusal code, and it is deliberately
   * flat: the server answers the same way whatever went wrong, so the button
   * cannot be used to discover which EdCity identities have composer access.
   *
   * What actually arrives here is the STUDENT case, linked or not. An unlinked
   * teacher still gets ?edconnect=2 and EdCityUnlinkedDialog ("log in & link",
   * or "create account"). A learner never does: the server answers not_linked
   * and this card tells them Composer is a teacher tool. The copy is the
   * single sentence that reader needs.
   */
  ssoErrorMessage() {
    const err = this.props.ssoError;
    if (err === 'not_linked') {
      return Localization.strings.SSO_REFUSED_NOT_LINKED;
    }
    if (err === 'access_denied') {
      return Localization.strings.SSO_REFUSED_CANCELLED;
    }
    if (err === 'upstream' || err === 'profile') {
      return Localization.strings.SSO_REFUSED_UPSTREAM;
    }
    return Localization.strings.THIRD_APRTY_LOGIN_FAILED;
  }

  handleEnterPressed(e) {
    if (e.key === "Enter") {
      this.loginClicked();
    }
  }

  // --- Forgot Password Handlers ---

  handleForgotPasswordClick() {
    this.ensureRecaptchaScript();
    this.setState({ showForgotPasswordDialog: true });
  }

  handleForgotPasswordClose() {
    this.setState({ showForgotPasswordDialog: false, forgotPasswordEmail: "" });
  }

  handleForgotPasswordEmailChange(e) {
    this.setState({ forgotPasswordEmail: e.target.value });
  }

  handleForgotPasswordSubmit() {
    // This is where the API call will be made.
    // We will assume a similar structure to the login call.
    if (!window.grecaptcha) {
      this.setState({
        showForgotPasswordDialog: false,
        forgotPasswordEmail: "",
        showConfirmationDialog: true,
        confirmationMessage: "reCAPTCHA is still loading. Please check your connection and try again in a moment."
      });
      return;
    }
    try {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute('6Lf2P2IrAAAAAGlJXkCwTqHvXG6Qrc_BJuD5gd0X', {action: 'forgot_password'}).then((token) => {
        this.props.parent.sendPasswordResetRequest(
          this.state.forgotPasswordEmail,
          token,
          () => { // onSuccess
            // Close the dialog and show a confirmation message
            this.setState({
              showForgotPasswordDialog: false,
              forgotPasswordEmail: "",
              showConfirmationDialog: true,
              confirmationMessage: "If an account with that email exists, a password reset link has been sent. Please check your inbox and spam folder."
            });
          },
          () => { // onFail
            this.setState({
              showForgotPasswordDialog: false,
              forgotPasswordEmail: "",
              showConfirmationDialog: true,
              confirmationMessage: "reCAPTCHA validation failed. You might be a robot. Please try again later."
            });
          }
        );
      });
    });
    } catch (e) {
      this.setState({
        showForgotPasswordDialog: false,
        forgotPasswordEmail: "",
        showConfirmationDialog: true,
        confirmationMessage: "reCAPTCHA validation failed. Please try again later."
      });
    }
  }

  handleConfirmationClose() {
    this.setState({ showConfirmationDialog: false });
  }

  // Toggle between the two supported interface languages (en <-> tc) so users
  // can switch to the language they know directly from the login screen.
  toggleLanguage() {
    const current = Localization.strings.getLanguage();
    const next = current === 'en' ? 'tc' : 'en';
    Localization.setLanguage(next);
    if (this.props.root && typeof this.props.root.languageChanged === 'function') {
      this.props.root.languageChanged();
    } else {
      this.forceUpdate();
    }
  }

  handleRememberMeChange = (event) => {
    this.setState({ rememberMe: event.currentTarget.checked });
  };

  render() {
    // (The API base used to be computed here to build the EdConnect button's
    // image URL. The image is bundled now, so nothing in this render reaches
    // across to the API host.)
    const showEdConnectLogin = typeof Config.ENABLE_EDCONNECT_LOGIN === 'function'
      ? Config.ENABLE_EDCONNECT_LOGIN()
      : false;
    const showGoogleLogin = !this.state.googleUnavailable
      && typeof Config.ENABLE_GOOGLE_LOGIN === 'function'
      && Config.ENABLE_GOOGLE_LOGIN();
    const showAppleLogin = typeof Config.ENABLE_APPLE_LOGIN === 'function'
      && Config.ENABLE_APPLE_LOGIN();
    // ONE "or" divider above the whole third-party block. Repeating it before
    // each provider — which is what the existing Google/EdConnect pair did —
    // reads as three separate alternatives to the password form rather than one
    // group of them.
    const showThirdParty = showGoogleLogin || showAppleLogin || showEdConnectLogin;

    // Language switcher: offer the OTHER language, labelled in its own script.
    const nextLang = Localization.strings.getLanguage() === 'en' ? 'tc' : 'en';
    const nextLangLabel = nextLang === 'en' ? 'English' : '繁體中文';

    return (
      <>
        {/* Main login card (always shown), centered over the LoginPage gradient.
            overflowY:auto + margin:auto keeps it centered when it fits and
            scrollable (no top-clip) when the card is taller than the viewport. */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', overflowY: 'auto', padding: 16 }}>
          <Paper shadow="md" radius="md" style={{ width: 360, maxWidth: '100%', margin: 'auto', overflow: 'hidden' }}>
            {/* Brand header bar */}
            <Box style={{ backgroundColor: BRAND_PRIMARY, padding: '16px', textAlign: 'center' }}>
              <Text c="white" fw={700} fz="1.5rem" component="div">
                EduVenture-AI<sup>&reg;</sup> Composer
              </Text>
            </Box>

            <Box style={{ padding: 20, paddingTop: 30 }}>
              {this.props.ssoError && (
                <Alert
                  color="red"
                  variant="light"
                  mb="md"
                  withCloseButton
                  onClose={this.props.onDismissSsoError}
                  title={Localization.strings.SSO_REFUSED_TITLE}
                  icon={<IconAlertCircle size={18} />}
                >
                  {/* pre-line, so the newlines in the copy are honoured but
                      each line still wraps normally in a 360px card. */}
                  <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                    {this.ssoErrorMessage()}
                  </Text>
                </Alert>
              )}

              <TextInput
                size="md"
                label={Localization.strings.USERNAME + "/" + Localization.strings.EMAIL}
                id="login-username"
                name="username"
                value={this.state.username}
                onChange={this.usernameChanged}
                onKeyDown={this.handleEnterPressed}
                leftSection={<IconUserCircle size={20} style={{ color: HALF_TONE }} />}
              />

              <Box style={{ height: 10 }} />

              <PasswordInput
                size="md"
                label={Localization.strings.PASSWORD}
                id="login-password"
                name="password"
                value={this.state.password}
                onChange={this.passwordChanged}
                onKeyDown={this.handleEnterPressed}
                leftSection={<IconLock size={20} style={{ color: HALF_TONE }} />}
              />

              <Divider my={20} />

              <Group justify="space-between" align="center" wrap="nowrap">
                <Text size="sm" style={{ color: MUTED }}>Remember Me 保持登入狀態</Text>
                <Switch
                  checked={this.state.rememberMe}
                  onChange={this.handleRememberMeChange}
                />
              </Group>

              <Button mt="md" fullWidth onClick={this.loginClicked}>
                {Localization.strings.LOGIN}
              </Button>

              {showThirdParty && (
                <>
                  <Group gap={8} my={18} align="center" wrap="nowrap">
                    <Box style={{ flex: 1, height: 1, background: DIVIDER }} />
                    <Text size="xs" style={{ color: MUTED }}>
                      {Localization.strings.THIRD_PARTY_OR}
                    </Text>
                    <Box style={{ flex: 1, height: 1, background: DIVIDER }} />
                  </Group>

                  {/* Each provider is 280px wide and centred, so the three
                      stack as one column rather than a ragged pile. */}
                  <Box
                    style={{
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 10,
                    }}
                  >
                    {showGoogleLogin && (
                      /* GIS renders the official button in here */
                      <div ref={this.googleButtonContainer} />
                    )}

                    {showAppleLogin && (
                      <AppleSignInButton
                        onClick={this.appleLoginClicked}
                        disabled={this.state.appleBusy}
                        label={Localization.strings.LOGIN_WITH_APPLE}
                      />
                    )}

                    {showEdConnectLogin && (
                      /* The official asset is the button — it is NOT wrapped in
                         a Mantine frame. "EdConnect Login Button Guidelines"
                         (2026-08-13 ToolKit) §B: do not modify the colour,
                         proportions or spacing, and never alter it "except to
                         change the size with the correct proportion". So: no
                         border, no background, width auto, and height matched to
                         the 44px Google/Apple buttons beside it. */
                      <button
                        type="button"
                        onClick={this.edConnectLoginClicked}
                        aria-label={Localization.strings.LOGIN_WITH_EDCONNECT}
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          cursor: 'pointer', lineHeight: 0,
                        }}
                      >
                        <img
                          alt={Localization.strings.LOGIN_WITH_EDCONNECT}
                          src={Localization.strings.getLanguage() === 'en'
                            ? edconnectButtonEn : edconnectButtonTc}
                          style={{ height: 44, width: 'auto' }}
                        />
                      </button>
                    )}
                  </Box>
                </>
              )}

              <Group justify="center" gap="sm" mt="md" wrap="nowrap">
                <Anchor component="button" type="button" c="brand" size="sm" onClick={this.handleForgotPasswordClick}>
                  {Localization.strings.FORGET_PASSWORD || 'Forgot Password'}
                </Anchor>
                <Box style={{ width: 1, height: 14, background: DIVIDER }} />
                <Anchor component="button" type="button" c="brand" size="sm" onClick={this.toggleLanguage}>
                  {nextLangLabel}
                </Anchor>
              </Group>

              <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 20, marginBottom: 15 }}>
                <img alt="" style={{ width: '100%', padding: 0, maxWidth: 200 }} src={cuhk_clst} />
              </Box>

              <Text size="sm" ta="center" style={{ color: MUTED }}>
                Version 20261020 Copyright © 2026. All Rights Reserved <br/>
                Centre for Learning Sciences and Technologies <br/>
                The Chinese University of Hong Kong
              </Text>
            </Box>
          </Paper>
        </div>

        {/* Forgot Password Dialog */}
        <Modal
          opened={this.state.showForgotPasswordDialog}
          onClose={this.handleForgotPasswordClose}
          title="Forgot Your Password?"
          centered
          size="xs"
        >
          <Text size="sm" ta="center" mb="md">
            Enter your email address below and we'll send you a link to reset your password.
          </Text>
          <TextInput
            data-autofocus
            label="Email Address"
            type="email"
            value={this.state.forgotPasswordEmail}
            onChange={this.handleForgotPasswordEmailChange}
            onKeyDown={(e) => { if (e.key === 'Enter') this.handleForgotPasswordSubmit(); }}
          />
          <Group justify="flex-end" mt="lg">
            <Button variant="subtle" onClick={this.handleForgotPasswordClose}>
              Cancel
            </Button>
            <Button onClick={this.handleForgotPasswordSubmit}>
              Send Reset Link
            </Button>
          </Group>
          <Text size="xs" c="dimmed" ta="center" mt="md">
            This site is protected by reCAPTCHA and the Google{' '}
            <Anchor href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" size="xs">
              Privacy Policy
            </Anchor>
            {' '}and{' '}
            <Anchor href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" size="xs">
              Terms of Service
            </Anchor>
            {' '}apply.
          </Text>
        </Modal>

        {/* Confirmation Dialog */}
        <Modal
          opened={this.state.showConfirmationDialog}
          onClose={this.handleConfirmationClose}
          title="Request Sent"
          centered
          size="xs"
        >
          <Text size="sm">
            {this.state.confirmationMessage}
          </Text>
          <Group justify="flex-end" mt="lg">
            <Button onClick={this.handleConfirmationClose}>
              OK
            </Button>
          </Group>
        </Modal>
      </>
    )
  }
}

export default LoginDialog;
